import { Icon } from '@iconify/react/dist/iconify.js';
import { Checkbox, Modal } from '@mantine/core';
import { toast } from '../../../notifications/notifications';
import { useCallback, useEffect, useState } from 'react';
import { useAssignUsersToGroup, useRemoveUsersFromGroup } from '../../../../api/rbac-client/rbac-client.hooks';
import type { GroupMember, OrganizationUser, PermissionGroup } from '../../../../api/rbac-client/rbac-client.type';
import { secondaryBase } from '../../../../styles/design-tokens';
import { Button } from '../../../../ui-library/button/button';
import { Text } from '../../../../ui-library/typography/typography';
import { buildInitialState } from './groups-tab.helpers';
import type { MembershipState } from './groups-tab.helpers';
import { ModalActions, ModalDivider, ModalGroupSection, ModalGroupTitle } from './groups-tab.styles';

export function EditGroupsModal({
  opened,
  onClose,
  organizationId,
  groups,
  orgUsers,
  membersByGroupId,
}: {
  opened: boolean;
  onClose: () => void;
  organizationId: string;
  groups: PermissionGroup[];
  orgUsers: OrganizationUser[];
  membersByGroupId: Record<string, GroupMember[]>;
}) {
  const [membershipState, setMembershipState] = useState<MembershipState>({});
  const [isSaving, setIsSaving] = useState(false);
  const assignMutation = useAssignUsersToGroup();
  const removeMutation = useRemoveUsersFromGroup();

  const resetState = useCallback(() => {
    setMembershipState(buildInitialState(groups, membersByGroupId));
  }, [groups, membersByGroupId]);

  useEffect(() => {
    if (opened) {
      resetState();
    }
  }, [opened, resetState]);

  function handleToggle(groupType: string, email: string, checked: boolean) {
    setMembershipState((prev) => {
      const updated = { ...prev };
      const set = new Set(updated[groupType] ?? []);
      if (checked) {
        set.add(email);
      } else {
        set.delete(email);
      }
      updated[groupType] = set;
      return updated;
    });
  }

  function handleClose() {
    onClose();
  }

  async function handleSave() {
    const initialState = buildInitialState(groups, membersByGroupId);
    const mutations: Promise<unknown>[] = [];

    for (const group of groups) {
      const initial = initialState[group.group_type] ?? new Set<string>();
      const current = membershipState[group.group_type] ?? new Set<string>();

      const toAdd = [...current].filter((email) => !initial.has(email));
      const toRemove = [...initial].filter((email) => !current.has(email));

      if (toAdd.length > 0) {
        mutations.push(
          assignMutation.mutateAsync({
            organization_id: organizationId,
            group_type: group.group_type,
            user_emails: toAdd,
          }),
        );
      }
      if (toRemove.length > 0) {
        mutations.push(
          removeMutation.mutateAsync({
            organization_id: organizationId,
            group_type: group.group_type,
            user_emails: toRemove,
          }),
        );
      }
    }

    if (mutations.length === 0) {
      handleClose();
      return;
    }

    setIsSaving(true);
    const results = await Promise.allSettled(mutations);
    const failures = results.filter((r) => r.status === 'rejected');
    setIsSaving(false);

    if (failures.length === 0) {
      toast.success('Group memberships updated successfully.');
      handleClose();
    } else if (failures.length < results.length) {
      toast.warning(`${results.length - failures.length} update(s) succeeded, ${failures.length} failed.`);
      handleClose();
    } else {
      toast.error('Failed to update group memberships.');
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      withCloseButton
      centered
      size="md"
      padding="md"
      zIndex={1000}
      styles={{ header: { padding: '8px 12px 0', minHeight: 'unset' }, close: { marginTop: 4 } }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <Icon icon="material-symbols:edit" width={28} color={secondaryBase} />
        <Text weight="bold" size="large">
          Edit Groups
        </Text>
      </div>

      <ModalDivider />

      <div style={{ maxHeight: 400, overflowY: 'auto' }}>
        {groups.map((group) => (
          <ModalGroupSection key={group.id}>
            <ModalGroupTitle>{group.group_type_display}</ModalGroupTitle>
            {orgUsers
              .filter((user) => membershipState[group.group_type]?.has(user.email))
              .map((user) => (
                <Checkbox
                  key={user.id}
                  label={user.name || user.email}
                  checked
                  onChange={(event) => handleToggle(group.group_type, user.email, event.currentTarget.checked)}
                  style={{ marginBottom: 6 }}
                />
              ))}
          </ModalGroupSection>
        ))}
      </div>

      <ModalDivider />

      <ModalActions>
        <Button variant="outline" size="sm" radius="xl" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="primary" size="sm" radius="xl" onClick={handleSave} loading={isSaving}>
          Save Groups
        </Button>
      </ModalActions>
    </Modal>
  );
}
